import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Plus, Minus, MessageCircle, MapPin, Star } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl, whatsappLink } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCity, catPrefix, productSlug } from "../context/CityContext";
import { useSeoTitle } from "../context/SeoContext";
import { track } from "../lib/analytics";
import { ProductReviews } from "../components/ProductReviews";
import { useI18n } from "../i18n";

const TABS = ["Produit", "Labo", "Usage", "Avis"];

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { city, vendor, delay, setModalOpen } = useCity();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selTier, setSelTier] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("Produit");
  const [mainImg, setMainImg] = useState("");

  useEffect(() => {
    setNotFound(false);
    api.get(`/products/by-slug/${slug}`, { params: vendor?.id ? { vendor_id: vendor.id } : {} })
      .then((r) => {
        setData(r.data);
        const p = r.data.product;
        setSelTier(p.tiers?.length ? p.tiers[0] : null);
        setMainImg(p.image_url || "");
      })
      .catch(() => setNotFound(true));
  }, [slug, vendor?.id]);

  const p = data?.product;
  useSeoTitle("product", { produit: p?.name || "", categorie: p?.category || "", extrait: (p?.description || "").slice(0, 120) }, !!p);
  useEffect(() => { if (p?.id) track("product_view", { meta: { product: p.name } }); }, [p?.id, p?.name]);

  useEffect(() => {
    if (!p) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "product-jsonld";
    s.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Product", name: p.name,
      description: p.description, image: imageUrl(p.image_url) || undefined,
      brand: { "@type": "Brand", name: "STUFF CBD" },
      offers: { "@type": "Offer", priceCurrency: "EUR", price: (p.tiers?.[0]?.price ?? p.price), availability: "https://schema.org/InStock" },
    });
    document.getElementById("product-jsonld")?.remove();
    document.head.appendChild(s);
    return () => s.remove();
  }, [p]);

  const requireCity = useCallback(() => {
    if (!city || !vendor) { setModalOpen(true); return false; }
    return true;
  }, [city, vendor, setModalOpen]);

  if (notFound) return (
    <div className="max-w-lg mx-auto px-4 py-20 text-center">
      <p className="text-zinc-400 mb-6" data-testid="product-not-found">Produit introuvable.</p>
      <Link to="/" className="bg-neon-green text-black font-anton uppercase px-6 py-3">← Retour au QG</Link>
    </div>
  );
  if (!p) return <div className="max-w-lg mx-auto px-4 py-20 text-center text-zinc-500">Chargement…</div>;

  const soldOut = !!vendor && p.in_stock === false;
  const gallery = [p.image_url, ...(p.gallery || [])].filter(Boolean).slice(0, 5);
  const unitPrice = selTier ? selTier.price : Number(p.price);
  const originalUnit = selTier ? Number(selTier.original_price ?? selTier.price) : Number(p.original_price ?? p.price);
  const total = (unitPrice * qty).toFixed(2);
  const variantLabel = selTier ? ` ${selTier.label}` : "";

  const addToCart = () => {
    if (!requireCity()) return;
    if (soldOut) { toast.error(t("product.soldOutAt")); return; }
    if (p.quantity > 0 && qty > p.quantity) { toast.error(`${t("co.stockShort")} ${p.quantity}`); return; }
    for (let i = 0; i < qty; i++) {
      addItem(selTier ? { ...p, selectedTier: selTier } : p, vendor);
    }
    toast.success(`${p.name}${variantLabel} x${qty} ajouté au panier 🛒`);
  };

  const waMsg = `COMMAND STUFF\nVille: ${city}\nProduit: ${p.name}${variantLabel} x${qty} — ${total}EUR\nPaiement a la livraison`;
  const wa = vendor ? whatsappLink(vendor.whatsapp || vendor.phone, waMsg) : null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="product-page">
      <p className="text-zinc-600 text-xs uppercase mb-4">
        <Link to="/" className="hover:text-neon-green">Accueil</Link> / <span className="text-zinc-400">{p.category}</span> / <span className="text-white">{p.name}</span>
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <div className="relative aspect-square bg-black border border-zinc-800">
            <img src={imageUrl(mainImg) || "/stuff-logo.png"} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.src = "/stuff-logo.png"; e.target.className = "w-full h-full object-contain p-10"; }} />
            <span className="absolute top-3 left-3 px-2 py-1 text-[10px] uppercase font-bold text-neon-magenta border border-neon-magenta rounded-full bg-black/60">{p.category}</span>
            {p.cbd_rate && <span className="absolute top-3 right-3 bg-black/80 border border-neon-green/60 text-neon-green text-xs font-bold px-2 py-1">CBD {p.cbd_rate}</span>}
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 mt-2" data-testid="page-gallery">
              {gallery.map((g) => (
                <button key={g} type="button" onClick={() => setMainImg(g)} className={`h-16 w-16 border-2 ${mainImg === g ? "border-neon-green" : "border-zinc-800"}`}>
                  <img src={imageUrl(g)} alt="" className="h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <h1 className="font-anton text-3xl sm:text-4xl uppercase text-white leading-tight">{p.name}</h1>
          <div className="flex flex-wrap gap-2 text-xs uppercase font-bold">
            <span className="text-zinc-300 border border-zinc-600 px-2 py-1">THC {p.thc_rate || "< 0,3%"}</span>
            {p.culture_type && <span className="text-zinc-300 border border-zinc-600 px-2 py-1">🌱 {p.culture_type}</span>}
            {p.origin && <span className="text-zinc-300 border border-zinc-600 px-2 py-1">📍 {p.origin}</span>}
          </div>
          <p className="text-zinc-400 text-sm">{p.description}</p>
          <div className="flex items-end gap-3 flex-wrap">
            <span className="font-anton text-4xl neon-text-green" data-testid="page-price">{unitPrice.toFixed(2)}€</span>
            {p.promo && originalUnit > unitPrice && <span data-testid="page-original-price" className="text-zinc-500 line-through text-lg mb-1">{originalUnit.toFixed(2)}€</span>}
            {p.promo && <span data-testid="page-promo-badge" className="mb-1.5 px-2.5 py-1 text-xs uppercase font-bold text-black bg-neon-magenta rounded-full glow-magenta">PROMO -{Math.round(p.promo_pct)}%</span>}
            {selTier && (selTier.units || 1) > 1 && <span className="text-zinc-500 text-xs mb-1.5">{(selTier.price / selTier.units).toFixed(2)}€/g</span>}
          </div>
          {p.promo && p.promo_until && <p data-testid="page-promo-until" className="text-neon-magenta text-xs font-semibold -mt-2">⏳ Offre valable jusqu'au {new Date(p.promo_until).toLocaleDateString("fr-FR")}</p>}
          {p.tiers?.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="page-tiers">
              {p.tiers.map((x) => (
                <button key={x.label} type="button" data-testid="page-tier-option" onClick={() => setSelTier(x)}
                  className={`border px-2 py-2.5 text-center transition-colors ${selTier?.label === x.label ? "border-neon-green bg-neon-green/10" : "border-zinc-700 hover:border-neon-green/60"}`}>
                  <span className={`font-anton block ${selTier?.label === x.label ? "text-neon-green" : "text-white"}`}>{x.label}</span>
                  <span className="text-zinc-400 text-xs">{x.original_price > x.price && <span className="line-through text-zinc-600 mr-1">{Number(x.original_price).toFixed(2)}€</span>}{Number(x.price).toFixed(2)}€</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="flex items-center border border-zinc-700">
              <button data-testid="page-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="h-11 w-11 flex items-center justify-center text-white hover:text-neon-green"><Minus className="h-4 w-4" /></button>
              <span className="w-8 text-center text-white font-bold" data-testid="page-qty">{qty}</span>
              <button data-testid="page-qty-plus" onClick={() => setQty(qty + 1)} className="h-11 w-11 flex items-center justify-center text-white hover:text-neon-green"><Plus className="h-4 w-4" /></button>
            </div>
            {soldOut ? (
              <button data-testid="page-sold-out-btn" disabled aria-disabled="true" className="flex-1 bg-zinc-800 text-zinc-500 border border-zinc-700 font-anton uppercase py-3 cursor-not-allowed">
                {t("product.soldOut")}
              </button>
            ) : (
              <button data-testid="page-add-btn" onClick={addToCart} className="flex-1 bg-neon-green text-black font-anton uppercase py-3 glow-green hover:bg-neon-greenDark transition-colors">
                Ajouter — {total}€
              </button>
            )}
          </div>
          {soldOut && <p data-testid="page-sold-out-msg" className="text-zinc-400 text-xs -mt-1">{t("product.soldOutAt")} — reviens bientôt ou choisis un autre produit ci-dessous.</p>}
          {/* WhatsApp : mobile uniquement — sur PC, seul le panier est proposé */}
          <div className="md:hidden" data-testid="page-whatsapp-wrap">
          {soldOut ? (
            <button data-testid="page-whatsapp-btn" disabled aria-disabled="true" className="w-full inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-600 font-anton uppercase py-3 cursor-not-allowed">
              <MessageCircle className="h-4 w-4" /> Commander sur WhatsApp
            </button>
          ) : wa ? (
            <a data-testid="page-whatsapp-btn" href={wa} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 border border-neon-green text-neon-green font-anton uppercase py-3 hover:bg-neon-green hover:text-black transition-colors">
              <MessageCircle className="h-4 w-4" /> Commander sur WhatsApp
            </a>
          ) : (
            <button data-testid="page-whatsapp-locked" onClick={() => setModalOpen(true)} className="w-full inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-400 font-anton uppercase py-3 hover:border-neon-green hover:text-neon-green transition-colors">
              <MessageCircle className="h-4 w-4" /> Choisis ta ville pour commander
            </button>
          )}
          </div>
          <div className="flex items-center gap-2 text-sm" data-testid="page-delivery-slot">
            <MapPin className="h-4 w-4 text-neon-magenta shrink-0" />
            {city ? (
              <span className="text-zinc-300">Livraison à <span className="text-neon-green font-bold">{city}</span> en <span className="text-neon-green font-bold">~{delay || "moins de 120"} min</span> · paiement cash à la livraison</span>
            ) : (
              <button onClick={() => setModalOpen(true)} className="text-neon-green underline">Choisis ta ville pour voir ton délai de livraison</button>
            )}
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="mt-10">
        <div className="flex border-b border-zinc-800" data-testid="page-tabs">
          {TABS.map((tb) => (
            <button key={tb} data-testid={`page-tab-${tb.toLowerCase()}`} onClick={() => setTab(tb)}
              className={`px-4 py-2.5 text-sm uppercase font-bold transition-colors ${tab === tb ? "text-neon-green border-b-2 border-neon-green" : "text-zinc-500 hover:text-white"}`}>{tb}</button>
          ))}
        </div>
        <div className="py-5 text-zinc-300 text-sm" data-testid="page-tab-content">
          {tab === "Produit" && <p className="whitespace-pre-line">{p.long_description || p.description}</p>}
          {tab === "Labo" && (p.lab_url
            ? <a href={imageUrl(p.lab_url)} target="_blank" rel="noopener noreferrer" className="text-neon-magenta underline">🔬 Voir l'analyse laboratoire (COA)</a>
            : <p className="text-zinc-500">Analyses laboratoire disponibles sur demande auprès de ton livreur. Chanvre légal &lt; 0,3% THC.</p>)}
          {tab === "Usage" && <p className="whitespace-pre-line">{p.usage_tips || "Produit réservé aux adultes (18+). Tenir hors de portée des enfants. Ne pas conduire après consommation."}</p>}
          {tab === "Avis" && <ProductReviews productId={p.id} />}
        </div>
      </div>

      {/* Produits liés */}
      {data.related?.length > 0 && (
        <div className="mt-8" data-testid="related-products">
          <h2 className="font-anton text-xl uppercase text-white mb-4">Tu vas aussi <span className="neon-text-magenta">kiffer</span></h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {data.related.map((r) => (
              <Link key={r.id} to={`/${catPrefix(r.category)}/${productSlug(r.name)}`} data-testid="related-product-link" className={`bg-ink-surface border border-zinc-800 hover:border-neon-green/60 transition-colors relative ${r.in_stock === false ? "opacity-60" : ""}`}>
                <img src={imageUrl(r.image_url) || "/stuff-logo.png"} alt={r.name} className={`w-full h-28 object-cover ${r.in_stock === false ? "grayscale" : ""}`} loading="lazy" onError={(e) => { e.target.src = "/stuff-logo.png"; e.target.className = "w-full h-28 object-contain p-4"; }} />
                <div className="p-2.5">
                  <p className="text-white text-sm font-bold line-clamp-2 leading-tight">{r.name}</p>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <p className="text-neon-green font-anton">{Number(r.price).toFixed(2)}€</p>
                    {r.in_stock === false ? (
                      <span data-testid="related-sold-out" className="bg-zinc-800 border border-zinc-700 text-zinc-500 text-[10px] font-anton uppercase px-2 py-1">{t("product.soldOut")}</span>
                    ) : r.in_stock === true ? (
                      <span data-testid="related-add" className="bg-neon-green text-black text-[10px] font-anton uppercase px-2 py-1">+ {t("product.add")}</span>
                    ) : null}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
